import xhr from 'xhr';
import cuid from 'cuid';
import MobileDetect from 'mobile-detect';

const checkWindow = () => typeof window !== 'undefined';

function hasLocalStorage() {
  try {
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
      return false;
    }
    return true;
  } catch (e) {
    console.warn('Local storage is disabled.', e);
    return false;
  }
}

function getDeviceId() {
  if (!hasLocalStorage()) {
    return cuid();
  }

  let deviceId = window.localStorage.getItem(DEVICE_ID_KEY);
  if (typeof deviceId === 'undefined' || deviceId === null) {
    deviceId = cuid();
    window.localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
}

function getFromSceneJson(key, def) {
  if (!checkWindow()) return def;
  const res = ((window || {}).sceneJson || {})[key];
  if (typeof res === 'undefined') {
    return def;
  }
  return res;
}

function isVisible() {
  if (!checkWindow() || typeof document.visibilityState === 'undefined') return true;
  return document.visibilityState === 'visible';
}

const DEVICE_ID_KEY = '__DOARAMA_DEVICE_ID';

// buffer of events that need to be flushed to the server.
let events = [];

// The trcking token is inserted into the page by the server in order to track which events
// come from which page load.
const trackingToken = getFromSceneJson('trackingToken');

// Our path for sending back the analytics data.
const analyticsEndpoint = getFromSceneJson('analyticsEndpoint', '/events');

// Our local clock at page load time. We report events
// with respect to this base. The server may choose to translate those into
// (roughly) global time by adding the event report time to the issued
// time in our tracker token.
const localClock = Date.now();

// How often we'er going to flush our events buffer to the server.
const flushPeriod = getFromSceneJson('trackingPeriod', 10000);

// Are we reporting tacking information?
const tracking = typeof trackingToken !== 'undefined';

// Are we currently connected to the internet.
let online = checkWindow() ? navigator.onLine : false;

// A device id that ties events from multiple pages to the same browser
const deviceId = getDeviceId();

// Have we rendered a frame
let haveRendered = false;

let visible = isVisible();

const isIframe = checkWindow() ? window.self !== window.top : false;

const referrer = checkWindow() ? document.referrer : null;

const isMobile = checkWindow() ? new MobileDetect(window.navigator.userAgent).mobile() : null;

// Flush the current event buffer.
function flush() {
  if (!checkWindow() || !tracking || !online || events.length <= 0) return;

  const report = {
    token: trackingToken,
    deviceId: deviceId,
    agent: navigator.userAgent,
    localClock: localClock,
    url: window.location.href,
    referrer: referrer,
    embedded: isIframe,
    mobile: isMobile,
    events: events,
    version: process.env.gitHash,
  };

  events = [];

  xhr(
    {
      body: JSON.stringify(report),
      uri: analyticsEndpoint,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    },
    (err, resp, body) => {
      if (resp.statusCode !== 200) {
        console.warn('Failed to post tracking report:', resp.statusCode, body);
        // If failure occures, we probably want to try to
        // keep the events.
      }
    },
  );
}

let intervalTimeout = null;

function setFlushInterval() {
  if (!intervalTimeout) {
    intervalTimeout = setInterval(flush, flushPeriod);
  }
}

if (checkWindow()) {
  window.addEventListener('online', () => {
    online = true;
    // Add an extra flush, because we might be overflowing our internal
    // buffer.
    flush();
  });

  window.addEventListener('offline', () => {
    online = false;
  });

  window.addEventListener('visibilitychange', () => {
    visible = isVisible();
  });

  window.addEventListener(
    'unload',
    () => {
      const event = {
        type: 'PAGE_UNLOAD',
        timestamp: Date.now(),
      };
      events.push(event);
      flush();
    },
    false,
  );
}

function addPageLoadEvent() {
  const event = {
    type: 'PAGE_LOAD',
    timestamp: localClock,
  };
  events.push(event);
}

export function addPlayStartEvent() {
  const event = {
    type: 'PLAY_START',
    timestamp: Date.now(),
  };
  events.push(event);
  flush();
}

export function addPlayingEvent(state) {
  const now = Date.now();

  let event = {
    type: 'PLAYING',
    timestamp: now,
  };
  events.push(event);

  event = {
    type: 'LOAD_STARTED',
    timestamp: state.loading.userEngaged.completionTime,
  };
  events.push(event);

  event = {
    type: 'LOAD_FINISHED',
    timestamp: now,
  };
  events.push(event);

  flush();
}

addPageLoadEvent();

function addRendererEvent(startTime, endTime, fps, playTimeSum, visiblePlayTimeSum) {
  const event = {
    type: 'RENDERER_EVENT',
    timestamp: Date.now(),
    start: startTime,
    end: endTime,
    fps: fps,
    playTimeSum: playTimeSum,
    visiblePlayTimeSum: visiblePlayTimeSum,
  };
  events.push(event);
}
// We want a report on which parts of the track have been played
// At what frame rate were they rendered.

function sameCesiumTime(t1, t2) {
  return t1.dayNumber === t2.dayNumber && t1.secondsOfDay === t2.secondsOfDay;
}

let thisSecondStats;
let lastCesiumTime;
let playTime;
let visiblePlayTime;
let lastRenderTime;

export const trackRender = time => {
  setFlushInterval();
  const now = Date.now();

  if (!haveRendered) {
    haveRendered = true;
    playTime = 0;
    visiblePlayTime = 0;
    lastRenderTime = now;
    lastCesiumTime = time;
  }

  if (typeof thisSecondStats === 'undefined') {
    thisSecondStats = {
      start: now,
      frameCount: 0,
      cesiumTime: time,
    };
  }

  thisSecondStats.frameCount += 1;

  // We're playing
  if (!sameCesiumTime(lastCesiumTime, time)) {
    playTime += now - lastRenderTime;
    if (visible) visiblePlayTime += now - lastRenderTime;
  }

  if (thisSecondStats.start < now - 1000) {
    // Record a tracking event.
    const fps = (thisSecondStats.frameCount * 1000) / (now - thisSecondStats.start);
    addRendererEvent(thisSecondStats.start, now, fps, playTime, visiblePlayTime);
    thisSecondStats = {
      start: now,
      frameCount: 0,
      cesiumTime: time,
    };
  }

  lastCesiumTime = time;
  lastRenderTime = now;
};

let endScreenReached = false;

export const addEndScreenEvent = () => {
  setFlushInterval();
  if (endScreenReached) return;
  endScreenReached = true;
  const event = {
    type: 'END_SCREEN',
    timestamp: Date.now(),
  };
  events.push(event);
  flush();
};

export const addClientApiEvent = (method, clientVersion, apiKey, location) => {
  const event = {
    type: 'CLIENT_API_EVENT',
    event: method,
    clientVersion,
    apiKey,
    location,
    timestamp: Date.now(),
  };
  events.push(event);
};
